import { ALBUMS } from "../data/albums";
import { songsByAlbum } from "../data";
import { displayDimension } from "./dimensions";
import { buildOfficialResult } from "./result";
import { selectedSongs } from "./selection";
import { getRuntimeShareUrl } from "../share";
import { INTERNAL_DIMENSIONS, type SelectionMap } from "../types";

const VERSION = "v1";
const CODE_PATTERN = /^v1(?:\.[0-9a-z]{1,2}){9}$/;

export function encodeMatchSelections(selections: SelectionMap): string {
  const indices = ALBUMS.map((album) => {
    const songs = songsByAlbum.get(album.name) ?? [];
    const index = songs.findIndex((item) => item.song === selections[album.name]);
    if (index < 0) throw new Error(`未完成 ${album.name} 的选择`);
    return index.toString(36);
  });
  return [VERSION, ...indices].join(".");
}

export function decodeMatchSelections(code: string): SelectionMap | null {
  if (!CODE_PATTERN.test(code)) return null;
  const indices = code.split(".").slice(1);
  const choices: SelectionMap = {};
  for (let index = 0; index < ALBUMS.length; index += 1) {
    const album = ALBUMS[index];
    const songIndex = Number.parseInt(indices[index], 36);
    const song = (songsByAlbum.get(album.name) ?? [])[songIndex];
    if (!song || !Number.isInteger(songIndex)) return null;
    choices[album.name] = song.song;
  }
  return encodeMatchSelections(choices) === code ? choices : null;
}

function publicUrl(hash: string) {
  const url = new URL(getRuntimeShareUrl());
  url.hash = hash;
  return url.toString();
}

export function matchInvitationUrl(ownerCode: string) {
  if (!decodeMatchSelections(ownerCode)) throw new Error("邀请记录无效");
  return publicUrl(`/match/${ownerCode}`);
}

export function matchResultUrl(ownerCode: string, friendCode: string) {
  if (!decodeMatchSelections(ownerCode) || !decodeMatchSelections(friendCode)) throw new Error("比较记录无效");
  return publicUrl(`/match-result/${ownerCode}/${friendCode}`);
}

export function compareSelections(owner: SelectionMap, friend: SelectionMap) {
  const ownerSongs = selectedSongs(owner);
  const friendSongs = selectedSongs(friend);
  if (ownerSongs.length !== ALBUMS.length || friendSongs.length !== ALBUMS.length) {
    throw new Error("双方都需要完成九张专辑的选择");
  }
  const ownerResult = buildOfficialResult(ownerSongs);
  const friendResult = buildOfficialResult(friendSongs);
  const shared = ALBUMS.filter((album) => owner[album.name] === friend[album.name]);
  const dimensions = INTERNAL_DIMENSIONS.map((dimension) => ({
    name: displayDimension(dimension),
    owner: Math.round(ownerResult.classification.percentiles[dimension]),
    friend: Math.round(friendResult.classification.percentiles[dimension]),
  }));
  const dimensionSimilarity = dimensions.reduce((sum, item) => sum + (100 - Math.abs(item.owner - item.friend)), 0) / dimensions.length;
  // An entertainment score, not a psychological measurement.
  const score = Math.round((shared.length / ALBUMS.length) * 60 + dimensionSimilarity * 0.4);
  return {
    score,
    shared,
    exactMatches: shared.length,
    dimensions,
    ownerPersona: ownerResult.persona.title,
    friendPersona: friendResult.persona.title,
    rows: ALBUMS.map((album) => ({
      album,
      ownerSong: owner[album.name],
      friendSong: friend[album.name],
      same: owner[album.name] === friend[album.name],
    })),
  };
}
